import { useCallback, useEffect, useState } from 'react'
import { Table, type Column } from 'grabbitbot-legacy-ui'
import { adminClient } from '../../api'
import type { CreditBalanceRow, CreditTransactionRow } from '../../api/adminTypes'
import { formatDateTime } from '../trading/tradingConstants'
import { AdminNotice, EmailLookupForm } from './adminShared'
import { describeAdminError, formatMoney, useEmailParam } from './adminHelpers'
/**
 * Admin view of a user's ARCHIVED local credit ledger (legacy
 * `AdminUserCreditInfoComponent`, route `admin/users/credit`, contract #17-18).
 * READ ONLY since the owner decision 2026-10-25 (plan 2026-10-17 D2 = A, BT
 * 705be4f9): the local GrabbitBot credit ledger is a frozen financial archive,
 * FeeSubscription holds the live balance. The add / remove credit form is gone
 * (the backend answers 410 `local_credit_ledger_retired` to both writes).
 */
export function AdminUserCreditPage() {
  const [email, setEmail] = useEmailParam()
  const [balances, setBalances] = useState<CreditBalanceRow[] | null>(null)
  const [loadFeedback, setLoadFeedback] = useState<ReturnType<typeof describeAdminError> | undefined>()
  const [expanded, setExpanded] = useState<string | null>(null)

  const loadBalances = useCallback((targetEmail: string) => {
    setBalances(null)
    setLoadFeedback(undefined)
    adminClient
      .creditAll(targetEmail)
      .then((rows) => setBalances(rows))
      .catch((err) => setLoadFeedback(describeAdminError(err)))
  }, [])

  useEffect(() => {
    if (email) loadBalances(email)
    else setBalances(null)
  }, [email, loadBalances])

  const balanceColumns: Column<CreditBalanceRow>[] = [
    { key: 'currency', label: 'Currency', align: 'left', sortValue: (r) => r.currency },
    {
      key: 'balance',
      label: 'Balance',
      align: 'right',
      numeric: true,
      sortValue: (r) => r.balance,
      format: (_v, r) => formatMoney(r.balance, r.currency),
    },
    { key: 'createdAt', label: 'Created', align: 'left', sortValue: (r) => r.createdAt, format: (v) => formatDateTime(v as string) },
    { key: 'lastModAt', label: 'Last change', align: 'left', sortValue: (r) => r.lastModAt, format: (v) => formatDateTime(v as string) },
    {
      key: 'expand',
      label: '',
      sortable: false,
      format: (_v, r) => (
        <button type="button" onClick={() => setExpanded((cur) => (cur === r.currency ? null : r.currency))}>
          {expanded === r.currency ? 'hide transactions' : `show transactions (${r.transactions.length})`}
        </button>
      ),
    },
  ]

  const txColumns: Column<CreditTransactionRow>[] = [
    { key: 'createdAt', label: 'Date', align: 'left', sortValue: (r) => r.createdAt, format: (v) => formatDateTime(v as string) },
    { key: 'amount', label: 'Amount', align: 'right', numeric: true, sortValue: (r) => r.amount, format: (_v, r) => formatMoney(r.amount, r.currency) },
    { key: 'balanceBefore', label: 'Before', align: 'right', numeric: true, sortValue: (r) => r.balanceBefore, format: (_v, r) => formatMoney(r.balanceBefore, r.currency) },
    { key: 'balanceAfter', label: 'After', align: 'right', numeric: true, sortValue: (r) => r.balanceAfter, format: (_v, r) => formatMoney(r.balanceAfter, r.currency) },
    { key: 'cause', label: 'Cause', align: 'left', sortValue: (r) => r.cause },
    { key: 'reference', label: 'Reference', align: 'left', sortValue: (r) => r.reference },
  ]

  return (
    <div>
      <div className="portal-page-title">
        <h1>Admin — user credit (archive)</h1>
      </div>
      <div className="portal-card">
        <p className="portal-notice portal-notice--info" role="note">
          This is the archived local GrabbitBot credit ledger: read only, never changed or deleted. Live balances are in
          FeeSubscription (the Payment pages); credit can no longer be added or removed here.
        </p>
        <EmailLookupForm email={email} onSubmit={setEmail} />
        <AdminNotice feedback={loadFeedback} />

        {!email && <p className="portal-text-muted">Enter a user e-mail to look up their credit balances.</p>}

        {email && (
          <>
            {balances === null && !loadFeedback && <p>Loading…</p>}
            {balances !== null && (
              <div className="portal-table-scroll">
                <Table
                  columns={balanceColumns}
                  rows={balances}
                  rowKey={(r) => r.currency}
                  renderExpanded={(r) =>
                    expanded === r.currency ? (
                      <tr className="portal-detail-row">
                        <td colSpan={balanceColumns.length}>
                          {r.transactions.length === 0 ? (
                            <p className="portal-text-muted">No transactions.</p>
                          ) : (
                            <Table columns={txColumns} rows={r.transactions} rowKey={(t) => String(t.id)} />
                          )}
                        </td>
                      </tr>
                    ) : null
                  }
                />
              </div>
            )}

          </>
        )}
      </div>
    </div>
  )
}
